import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DonutChart as TremorDonutChart } from "@tremor/react";
import {
  TableColumn,
  StandardizedTable,
  formatNumber,
  formatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import { getDevicesSalesApiV1DevicesSalesGet } from "@/backend-sdk/client/sdk.gen";
import type { DevicesSalesResponse } from "@/backend-sdk/client/types.gen";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { Skeleton } from "@/components/ui/skeleton";
import { useDrilldownOptional } from "@/features/drilldown";
import { heyApiClient } from "@/lib/hey-api-client";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";

/** Sessões, receita e conversão por dispositivo saem do GA4. */
const FONTES_DA_TABELA: readonly RequisitoDeFonte[] = ["ga4"];

type DeviceDonutRow = {
  name: string;
  percent: number;
  fill: string;
};

const VendasDispositivosCard: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const drilldown = useDrilldownOptional();
  // Começa carregando: com `false`, o primeiro desenho pulava o esqueleto e mostrava o card vazio.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devicesData, setDevicesData] = useState<DevicesSalesResponse | null>(
    null,
  );

  // Handler para clique na linha (cross-filtering com Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const deviceName = row.dispositivo as string;
      if (!deviceName || deviceName === "N/D") return;

      const isMultiSelect = !!(event?.ctrlKey || event?.metaKey);
      drilldown.selectRow("device", deviceName, deviceName, isMultiSelect);
    },
    [drilldown],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const deviceName = row.dispositivo as string;
      return drilldown.isSelected("device", deviceName);
    },
    [drilldown],
  );

  const fetchData = useCallback(async () => {
    if (!globalFilter) return;
    try {
      setLoading(true);
      setError(null);
      const startDate = formatDateToISO(globalFilter.dateRange.startDate);
      const endDate = formatDateToISO(globalFilter.dateRange.endDate);
      const comparison = globalFilter.comparison?.type || "previous_period";

      const { data: response } = await getDevicesSalesApiV1DevicesSalesGet({
        client: heyApiClient,
        throwOnError: true,
        query: {
          start_date: startDate,
          end_date: endDate,
          comparison,
          customer_uuid:
            selectedAccount?.customer_uuid ||
            selectedAccount?.id ||
            localStorage.getItem("active_customer_uuid") ||
            undefined,
        },
      });

      setDevicesData(response || null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, globalFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchData();
  }, [fetchData]);

  const items = useMemo(() => devicesData?.items || [], [devicesData?.items]);
  const total = devicesData?.total_row;

  const sessionsTotal = useMemo(() => {
    return items.reduce((sum, i) => sum + Number(i.sessions || 0), 0);
  }, [items]);

  const revenueTotal = useMemo(() => {
    return items.reduce((sum, i) => sum + Number(i.total_revenue || 0), 0);
  }, [items]);

  const percentFormatter = useCallback((value: number) => {
    if (!Number.isFinite(value)) return "0%";
    return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }, []);

  const sessoesData = useMemo<DeviceDonutRow[]>(() => {
    return items.map((i) => {
      const raw = Number(i.sessions || 0);
      const percent = sessionsTotal > 0 ? (raw / sessionsTotal) * 100 : 0;
      return {
        name: i.device,
        percent,
        fill:
          i.device?.toLowerCase() === "mobile"
            ? "hsl(var(--success-soft))"
            : i.device?.toLowerCase() === "desktop"
              ? "hsl(var(--primary))"
              : "hsl(var(--accent))",
      };
    });
  }, [items, sessionsTotal]);

  const receitaData = useMemo<DeviceDonutRow[]>(() => {
    return items.map((i) => {
      const raw = Number(i.total_revenue || 0);
      const percent = revenueTotal > 0 ? (raw / revenueTotal) * 100 : 0;
      return {
        name: i.device,
        percent,
        fill:
          i.device?.toLowerCase() === "mobile"
            ? "hsl(var(--success-soft))"
            : i.device?.toLowerCase() === "desktop"
              ? "hsl(var(--primary))"
              : "hsl(var(--accent))",
      };
    });
  }, [items, revenueTotal]);

  const renderDeltaCell = (value: unknown): React.ReactNode => {
    const change = typeof value === "number" ? value : null;
    if (change === null || Number.isNaN(change)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return <TrendIndicator value={change} />;
  };

  const renderND = () => (
    <ValorIndisponivel fontes={FONTES_DA_TABELA} erro={error !== null} />
  );

  const columns: TableColumn[] = [
    {
      key: "dispositivo",
      header: "Categoria de dispositivo",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "sessoes",
      header: "Sessões",
      type: "number",
      render: (value) => (value === 0 ? renderND() : formatNumber(value)),
    },
    {
      key: "sessoes_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      type: "number",
      render: (value) => (value === 0 ? renderND() : formatCurrency(value)),
    },
    {
      key: "receitaTotal_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      type: "percent",
      visible: false,
      render: (value) => {
        const numeric = typeof value === "number" ? value : Number(value || 0);
        return !Number.isFinite(numeric) || numeric === 0
          ? renderND()
          : `${numeric.toFixed(2)}%`;
      },
    },
    {
      key: "txConversao_change",
      header: "% Δ",
      align: "right" as const,
      visible: false,
      render: renderDeltaCell,
    },
  ];

  const tableData = items.map((r) => ({
    dispositivo: r.device,
    sessoes: r.sessions,
    sessoes_change: r.sessions_change_percentage ?? null,
    receitaTotal: r.total_revenue,
    receitaTotal_change: r.total_revenue_change_percentage ?? null,
    txConversao: r.conversion_rate ?? 0,
    txConversao_change: r.conversion_rate_change_percentage ?? null,
  }));

  const totalRow = total
    ? {
        dispositivo: "Total Geral",
        sessoes: total.sessions || 0,
        sessoes_change: total.sessions_change_percentage ?? null,
        receitaTotal: total.total_revenue || 0,
        receitaTotal_change: total.total_revenue_change_percentage ?? null,
        txConversao: total.conversion_rate ?? 0,
        txConversao_change: total.conversion_rate_change_percentage ?? null,
      }
    : undefined;

  // Skeleton apenas no carregamento inicial (evita "pisca-pisca" em refetch)
  if (!devicesData && loading) {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle>VENDAS POR DISPOSITIVOS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="text-center">
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow relative">
      <CardHeader>
        <CardTitle>VENDAS POR DISPOSITIVOS</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Gráficos de Donut */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-2">Sessões</div>
            <div className="h-32">
              <TremorDonutChart
                className="h-full"
                data={sessoesData}
                index="name"
                category="percent"
                colors={["emerald", "blue", "violet"]}
                valueFormatter={percentFormatter}
                showLabel={false}
                showAnimation={true}
              />
            </div>
            <div className="text-xs mt-2 space-y-1">
              {sessoesData.map((item, idx) => (
                <div
                  key={`${item.name}-${idx}`}
                  className="flex items-center justify-center gap-2"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.fill }}
                  ></div>
                  <span className="text-muted-foreground">
                    {item.name?.toLowerCase?.() || item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-2">Receita</div>
            <div className="h-32">
              <TremorDonutChart
                className="h-full"
                data={receitaData}
                index="name"
                category="percent"
                colors={["emerald", "blue", "violet"]}
                valueFormatter={percentFormatter}
                showLabel={false}
                showAnimation={true}
              />
            </div>
            <div className="text-xs mt-2 space-y-1">
              {receitaData.map((item, idx) => (
                <div
                  key={`${item.name}-${idx}`}
                  className="flex items-center justify-center gap-2"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.fill }}
                  ></div>
                  <span className="text-muted-foreground">
                    {item.name?.toLowerCase?.() || item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabela integrada com engrenagem de colunas */}
        <StandardizedTable
          title=""
          columns={columns}
          data={tableData}
          loading={loading}
          total={totalRow || undefined}
          onRowClick={drilldown ? handleRowClick : undefined}
          selectedRowIndex={drilldown ? isRowSelected : undefined}
          clickableRows={!!drilldown}
          variant="plain"
          showHeader={true}
        />
      </CardContent>
      {loading && devicesData ? (
        <div className="absolute inset-0 rounded-lg bg-background/40 backdrop-blur-[1px] flex items-center justify-center">
          <div className="grid grid-cols-2 gap-4 w-full px-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export default VendasDispositivosCard;
